import { Component } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime } from 'rxjs';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import { UploadService } from 'src/app/services/UploadService/upload.service';
import { ServicoEntradaService } from 'src/app/services/ServicoEntradaService/servicoEntrada.service';
import { ITabelaCarreta } from './listagem-servicos-model';
import { formatarData, formatarPlaca } from 'src/app/core/utils/utilities';
import {
  IGetReportBase64,
  IReportServicosEntradaFilterParams
} from 'src/app/services/ReportService/report.model';
import { AppExportBase64 } from 'src/app/services/ExportBase64/exportBase64.service';
import { ImpressoraService } from 'src/app/services/ImpressoraService/impressora.service';
import { ReportService } from 'src/app/services/ReportService/report.service';
import {
  ICancelamentoServicoAfetado,
  ICancelamentoServicoEntradaPreview,
  IGetServicosParams
} from 'src/app/services/ServicoEntradaService/servicoEntrada.model';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';
import { validarPermissao } from 'src/app/core/utils/permissionScreen/permissionScreen';

@Component({
  selector: 'app-listagem-servicos',
  templateUrl: './listagem-servicos.component.html',
  styleUrls: ['./listagem-servicos.component.scss']
})
export class ListagemServicosComponent {
  form = this._inicializarFormulario();
  tableData: ITabelaCarreta[] | Array<any> = [];
  maxPages: number = 1;
  expandirCardsMobile: boolean = true;
  isLoading: boolean = false;

  situacaoOptions = [
    { name: 'Ativos', value: 0 },
    { name: 'Cancelados', value: 1 }
  ];

  tableColumns: any[] = this.buildColumns();

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private servicoEntradaService: ServicoEntradaService,
    private authService: AuthService,
    private uploadService: UploadService,
    private appExportBase64: AppExportBase64,
    private impressoraService: ImpressoraService,
    private reportService: ReportService,
    private swalAlert: SwalAlertService
  ) { }

  get isListagemCancelados(): boolean {
    return Number(this.form?.controls['FL_CNCL'].value) === 1;
  }

  buildColumns(): any[] {
    const columns: any[] = [
      { header: 'Cód. Serviço', key: 'codigoServico', type: 'text' },
      { header: 'Cód. Gate Entrada', key: 'codigoGateEntrada', type: 'text' },
      { header: 'Serviço', key: 'servico', type: 'text' },
      { header: 'Valor', key: 'valorServico', type: 'text' },
      { header: 'Entrada', key: 'entrada', type: 'text' },
      { header: 'Saída', key: 'saida', type: 'text' },
      { header: 'Placa Cavalo', key: 'placaCavalo', type: 'text' },
      { header: 'Motorista', key: 'motorista', type: 'text' },
      { header: 'Empresa', key: 'empresa', type: 'text' }
    ];

    if (this.isListagemCancelados) {
      // Mantém a mesma quantidade de colunas da listagem de ativos para não estourar a largura do card:
      // Gate Entrada, Saída e Motorista dão lugar aos dados do cancelamento (seguem disponíveis em Visualizar).
      const colunasOcultas = ['codigoGateEntrada', 'saida', 'motorista'];
      return [
        ...columns.filter(c => !colunasOcultas.includes(c.key)),
        { header: 'Cancelado em', key: 'dataCancelamento', type: 'text' },
        { header: 'Cancelado por', key: 'canceladoPor', type: 'text' },
        { header: 'Motivo', key: 'motivoCancelamento', type: 'text' },
        {
          header: 'Ações',
          key: 'actions',
          type: 'buttons',
          buttons: [
            {
              label: 'Visualizar',
              img: 'assets/eye-icon.png',
              action: (item: any) => this.visualizarServico(item)
            }
          ]
        }
      ];
    }

    return [
      ...columns,
      {
        header: 'Ações',
        key: 'actions',
        type: 'buttons',
        buttons: [
          {
            label: 'Editar',
            img: 'assets/edit.svg',
            action: (item: any) => this.editarServico(item),
            condition: (item: any) => item.saida == '---'
          },
          {
            label: 'Visualizar',
            img: 'assets/eye-icon.png',
            action: (item: any) => this.visualizarServico(item)
          },
          {
            label: 'Download Ticket',
            img: 'assets/icon-download.svg',
            action: (item: any) => this.downloadTicket(item)
          },
          {
            label: 'Impressão Ticket',
            img: 'assets/icon-print.svg',
            action: (item: any) => this.imprimirTicket(item)
          },
          {
            label: 'Cancelar',
            img: 'assets/cancel-icon.svg',
            action: (item: any) => this.cancelarServico(item),
            condition: () => validarPermissao('listaServico', 'deletar')
          }
        ]
      }
    ];
  }

  private _inicializarFormulario() {
    const form = this.fb.group({
      FK_SRVC_ENTR: this.fb.control('', { nonNullable: true }),
      FK_EPSA: this.fb.control('', { nonNullable: true }),
      NM_MTRT: this.fb.control('', { nonNullable: true }),
      TX_PLCA_CAVA: this.fb.control('', { nonNullable: true }),
      DT_SRVC_ENTR_INIC: this.fb.control('', { nonNullable: true }),
      DT_SRVC_ENTR_FIM: this.fb.control('', { nonNullable: true }),
      FL_CNCL: this.fb.control(0, { nonNullable: true }),
      PAGE_SIZE: this.fb.control(8, { nonNullable: true }),
      PAGE_NUMBER: this.fb.control(1, { nonNullable: true })
    });
    return form;
  }

  editarServico(item: any) {
    const queryParams = {
      ...item,
      isEditing: true
    };
    this.router.navigate(['cadastro/servicos'], {
      queryParams: queryParams
    });
  }

  visualizarServico(item: any) {
    const queryParams = {
      ...item,
      isViewing: true
    };
    this.router.navigate(['cadastro/servicos'], {
      queryParams: queryParams
    });
  }

  ngOnInit() {
    this.getServicos();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.getServicos();
    });
  }

  onSituacaoChange(event: number): void {
    if (this.form.controls['FL_CNCL'].value !== event) {
      this.form.controls['FL_CNCL'].setValue(event);
    }
    this.tableColumns = this.buildColumns();
    this.form.controls['PAGE_NUMBER'].setValue(1);
  }

  cancelarServico(item: any): void {
    const id = Number(item.codigoServico);
    this.isLoading = true;

    this.servicoEntradaService.GetCancelamentoServicoEntradaPreview(id).subscribe({
      next: preview => {
        this.isLoading = false;

        if (!preview.FL_PODE_CNCL) {
          this.swalAlert.showAlert(
            'Cancelamento não permitido',
            preview.TX_MOTV_BLOQ ?? 'Este serviço não pode ser cancelado.',
            'warning'
          );
          return;
        }

        this.swalAlert.showConfirmWithTextarea(
          `Cancelar serviço #${id}`,
          '',
          'Motivo do cancelamento',
          'Descreva o motivo do cancelamento',
          motivo => this.confirmarCancelamentoServico(id, motivo),
          'warning',
          this.montarResumoCancelamento(preview)
        );
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private confirmarCancelamentoServico(id: number, motivo: string): void {
    this.isLoading = true;

    this.servicoEntradaService
      .CancelServicoEntrada(id, { TX_MOTV_CNCL_SRVC_ENTR: motivo })
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.swalAlert.showAlert('Sucesso!', 'Serviço cancelado.', 'success');
          this.getServicos();
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  private montarResumoCancelamento(preview: ICancelamentoServicoEntradaPreview): string {
    const moeda = (valor: number) =>
      (valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const listaServicos = (servicos: ICancelamentoServicoAfetado[]) =>
      servicos
        .map(s => `<li>#${s.PK_SRVC_ENTR} – ${this.escaparHtml(s.TX_TIPO_SRVC ?? 'Serviço')}</li>`)
        .join('');

    const partes: string[] = [
      '<div style="text-align:left">',
      `<p><b>Serviços que serão cancelados:</b></p><ul>${listaServicos(preview.SERVICOS_CANCELADOS)}</ul>`
    ];

    if (preview.PAGAMENTOS_ESTORNO.length) {
      const pagamentos = preview.PAGAMENTOS_ESTORNO
        .map(p => `<li>${this.escaparHtml(p.TX_TIPO_PGMT ?? '')}: ${moeda(p.VL_PGMT_SAID)}${p.FK_CAIX ? ` (caixa #${p.FK_CAIX})` : ''}</li>`)
        .join('');
      partes.push(`<p><b>Estorno no caixa: ${moeda(preview.VL_TOTL_ESTORNO)}</b></p><ul>${pagamentos}</ul>`);
    } else {
      partes.push('<p>Não há pagamento registrado para estornar.</p>');
    }

    if (preview.SERVICOS_REABERTOS.length) {
      partes.push(
        `<p><b>Atenção:</b> estes serviços foram pagos junto e voltarão a ficar <b>em aberto</b> para nova cobrança:</p><ul>${listaServicos(preview.SERVICOS_REABERTOS)}</ul>`
      );
    }

    partes.push('</div>');
    return partes.join('');
  }

  private escaparHtml(texto: string): string {
    return texto
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  navegarNovaEntrada() {
    this.router.navigate(['cadastro/servicos']);
  }

  mudarPagina(page: number) {
    this.form.controls['PAGE_NUMBER'].setValue(page + 1);
    this.getServicos();
  }

  formatarCampos(campo: string | number | Date): string {
    return campo === null || campo === '' ? 'Não Informado' : String(campo);
  }

  getServicos() {
    this.isLoading = true;
    this.authService.dadoLogin$.subscribe(({ idEmpresa }) => {
      let params: IGetServicosParams = {
        ...this.form.value,
        FL_CNCL: this.isListagemCancelados
      };

      const placa = String(this.form.controls.TX_PLCA_CAVA.value ?? '')
        .replace(/-/g, '')
        .trim();

      params = {
        ...params,
        DT_SRVC_ENTR_INIC: this.form.controls.DT_SRVC_ENTR_INIC.value,
        DT_SRVC_ENTR_FIM: this.form.controls.DT_SRVC_ENTR_FIM.value,
        TX_PLCA_CAVA: placa || undefined
      };

      this.servicoEntradaService.GetServicos(params).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.tableData = response.ITEMS.map((item: any) => {
            return {
              codigoServico: item.PK_SRVC_ENTR ?? '---',
              codigoGateEntrada: item.FK_GATE_ENTR ?? '---',
              servico: item.TX_TIPO_SRVC ?? '---',
              valorServico:
                item.VL_SRVC.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }) ?? '---',
              entrada: formatarData(item.DT_SRVC_ENTR) ?? '---',
              saida: formatarData(item.DT_GATE_SAID) ?? '---',
              placaCavalo: formatarPlaca(item.TX_PLCA_CAVA) ?? '---',
              motorista: item.NM_MTRT ?? '---',
              empresa: item.NM_EPSA ?? '---',
              nuCaixSrvc: item.NU_CAIX_SRVC,
              dataCancelamento: formatarData(item.DT_CNCL_SRVC_ENTR) ?? '---',
              canceladoPor: item.NM_FUNC_CNCL ?? '---',
              motivoCancelamento: item.TX_MOTV_CNCL_SRVC_ENTR ?? '---'
            };
          });
          this.maxPages = response.TOTAL_PAGES;
        },
        error: (_error: any) => {
          this.isLoading = false;
        }
      });
    });
  }

  downloadExcel(): void {
    this.uploadService.downloadTemplateUpload('carreta');
  }

  permitirEditar(item: any): boolean {
    const status: boolean = item.situacao == 1 ? true : false;
    return status;
  }

  downloadTicket(item: any) {
    this.isLoading = true;
    const isHigienizacao = item.nuCaixSrvc === 2;

    this.servicoEntradaService
      .GetReceiptServicoEntradaById(
        0,
        0,
        Number(item.codigoServico),
        isHigienizacao
      )
      .subscribe({
        next: (response: IGetReportBase64) => {
          this.appExportBase64.downloadPDF(response.ReportBase64, 'ticket');
          this.isLoading = false;
        },
        error: _error => {
          this.isLoading = false;
        }
      });
  }

  imprimirTicket(item: any) {
    this.isLoading = true;
    const isHigienizacao = item.nuCaixSrvc === 2;

    this.servicoEntradaService
      .GetReceiptServicoEntradaById(
        0,
        1,
        Number(item.codigoServico),
        isHigienizacao
      )
      .subscribe({
        next: (response: IGetReportBase64) => {
          this.isLoading = false;
          this.impressoraService.impressoraTicket(
            response.ReportBase64,
            'text/plain'
          );
        },
        error: _error => {
          this.isLoading = false;
        }
      });
  }

  private buildReportServicoFilterParams(): IReportServicosEntradaFilterParams {
    const o: IReportServicosEntradaFilterParams = {};
    const epsaForm = String(this.form.controls['FK_EPSA'].value ?? '').trim();
    if (epsaForm && !Number.isNaN(Number(epsaForm)) && Number(epsaForm) > 0) {
      o.FK_EPSA = Number(epsaForm);
    }
    const srvcEntr = String(this.form.controls['FK_SRVC_ENTR'].value ?? '').trim();
    if (srvcEntr && !Number.isNaN(Number(srvcEntr)) && Number(srvcEntr) > 0) {
      o.FK_SRVC_ENTR = Number(srvcEntr);
    }
    const m = String(this.form.controls['NM_MTRT'].value ?? '').trim();
    if (m) {
      o.NM_MTRT = m;
    }
    const placa = String(this.form.controls['TX_PLCA_CAVA'].value ?? '')
      .replace(/-/g, '')
      .trim();
    if (placa) {
      o.TX_PLCA_CAVA = placa;
    }
    const di = this.form.controls['DT_SRVC_ENTR_INIC'].value;
    const df = this.form.controls['DT_SRVC_ENTR_FIM'].value;
    if (di && String(di).trim() !== '') {
      o.DT_SRVC_ENTR_INIC = String(di);
    }
    if (df && String(df).trim() !== '') {
      o.DT_SRVC_ENTR_FIM = String(df);
    }
    return o;
  }

  exportarExcel() {
    this.isLoading = true;
    this.reportService
      .GetReportServicosEntradas(this.buildReportServicoFilterParams())
      .subscribe({
        next: (response: IGetReportBase64) => {
          this.appExportBase64.downloadExcel(
            response.ReportBase64,
            'lista-servicos'
          );
          this.isLoading = false;
        },
        error: _error => {
          this.isLoading = false;
        }
      });
  }
}
